import { useCallback, useEffect, type ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useChrome, setSeedPanel } from './chromeStore'
import { NAV, ROUTES, crumbFor, type NavItem } from './nav'
import { SeedMeter, SeedPanel } from '../SeedChip'
import { useProfile, initialsOf } from '@/hooks/useProfile'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClient } from '@/hooks/useAuth'
import { usePlan } from '@/hooks/useConfig'
import { CoinCount } from '@/components/coins/CoinCount'
import { useNewsletterItems } from '@/hooks/useNewsletterItems'
import { useEmmaUI } from '@/components/emma/Emma'
import { useVoiceRing } from '@/components/voice/useVoiceRing'

/**
 * The desktop sidebar (desktop-nav.js): GoGrov, the account card (it opens
 * Settings, as the initials do on the phone), four groups (Create, Publish,
 * Feed controls, Account), Emma's row and the week's seed meter at the bottom.
 * Coming-soon items stay off the sidebar (desktop-nav.js v2.5: "keep it simple").
 *
 * v3.1 (Oct 29, Brian: "need to build it into the desktop so it matches the
 * app"): Emma has a row of her own above the meter, her seed and her name, as
 * she has a tab on the phone; it opens her window beside the sidebar.
 * v3.2 (Brian's desktop log #007): the meter reads "% left" and opens the seed
 * panel beside the sidebar. One panel at a time: opening one closes the other.
 */
export function DesktopSidebar({ emma }: { emma: ReactNode }) {
  const navKey = useChrome((s) => s.navKey)
  const { data: profile } = useProfile()
  const email = useAuthStore((s) => s.user?.email)
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const client = useEffectiveClient()
  const { data: plan } = usePlan()
  const panel = useChrome((s) => s.seedPanel)
  const emmaOpen = useEmmaUI((s) => s.open)
  // Brian (Nov 3): while the voice profile is filling, the line under Emma says so.
  const voice = useVoiceRing()
  const voiceLine = voice.ready && !voice.progress.full && voice.progress.total > 0
    ? `Voice profile: ${voice.progress.left} of ${voice.progress.total} left` : null
  const setEmmaOpen = useEmmaUI((s) => s.setOpen)
  // one panel at a time beside the sidebar
  useEffect(() => { if (emmaOpen) setSeedPanel(false) }, [emmaOpen])
  useEffect(() => { if (panel) useEmmaUI.getState().setOpen(false) }, [panel])
  // desktop-nav.js: This week's issue carries the issue's story count
  const nlN = useNewsletterItems().items.length
  const place = (client?.city_state ?? '').split(',')[0]

  const link = (it: NavItem, sub = false) => (
    <Link key={it.key} to={it.to} className={`gg-side__a${sub ? ' gg-side__a--sub' : ''}`}
      aria-current={navKey === it.key ? 'page' : undefined}>
      {it.label}{it.soon && <small>soon</small>}{it.key === 'achievements' && <CoinCount />}
      {it.key === 'newsletter.issue' && nlN > 0 && <span className="gg-side__pill">{nlN}</span>}
    </Link>
  )

  return (
    <aside className="gg-side" aria-label="GoGrov">
      <Link to={ROUTES.feed} className="gg-side__logo" aria-label="GoGrov home">
        <img src="/brand/gogrov-logo-horizontal.svg" alt="GoGrov" height={26} />
      </Link>
      <NavLink to={ROUTES.settings} className="gg-side__who" title="Settings">
        <span className="gg-side__dot">{initialsOf(profile?.full_name, email)}</span>
        <span className="gg-side__whotext">
          <b>{client?.display_name || profile?.full_name || ''}</b>
          <span>{[plan?.name, place].filter(Boolean).join(' · ')}</span>
        </span>
      </NavLink>
      <nav className="gg-side__nav">
        {NAV.filter((g) => !g.deskHidden && g.items.some((it) => !it.soon)).map((g) => (
          <div key={g.label} className="gg-side__group">
            <div className="gg-side__sec">{g.label}</div>
            {g.items.filter((it) => !it.soon).map((it) => (
              <div key={it.key}>
                {link(it)}
                {it.subs?.filter((s) => !s.soon).map((s) => link(s, true))}
              </div>
            ))}
          </div>
        ))}
        {isAdmin && (
          <div className="gg-side__group">
            <div className="gg-side__sec">Admin</div>
            <Link to="/admin" className="gg-side__a">Admin console</Link>
          </div>
        )}
      </nav>
      <div className={`gg-side__emma${emmaOpen ? ' is-open' : ''}`}>
        <span className="gg-side__ems">{emma}</span>
        <button type="button" className="gg-side__emtx" aria-expanded={emmaOpen} onClick={() => setEmmaOpen(!emmaOpen)}>
          <b>Emma</b><small>{voiceLine ?? 'Ask about this screen'}</small>
        </button>
      </div>
      <div className="gg-side__meter">
        <SeedMeter open={panel} onToggle={() => setSeedPanel(!panel)} />
        {panel && <SeedPanel placement="desk" onClose={() => setSeedPanel(false)} />}
      </div>
    </aside>
  )
}

/**
 * The one 48px toolbar on every desktop screen: "Group › Screen" on the left,
 * the screen's actions on the right. No phone bars, no repeated titles.
 */
export function DesktopToolbar() {
  const title = useChrome((s) => s.title)
  const navKey = useChrome((s) => s.navKey)
  const group = useChrome((s) => s.group)
  const actRef = useCallback((el: HTMLDivElement | null) => { useChrome.getState().set({ deskActions: el }) }, [])
  const c = crumbFor(navKey, title)
  const g = group ?? c.group
  return (
    <div className="gg-toolbar">
      {g && <><span className="gg-toolbar__grp">{g}</span><span className="gg-toolbar__chev" aria-hidden="true">›</span></>}
      <h1 className="gg-toolbar__name">{c.name}</h1>
      <div className="gg-toolbar__act" ref={actRef} />
    </div>
  )
}
